import { intentStatusMachine } from "@src/components/DefuseSDK/features/machines/intentStatusMachine"
import {
  type OneClickExpiredEvent,
  oneClickStatusMachine,
} from "@src/components/DefuseSDK/features/machines/oneClickStatusMachine"
import type { IntentDescription } from "@src/components/DefuseSDK/features/machines/swapIntentMachine"
import type { SwapJourneyProvenance } from "@src/components/DefuseSDK/features/machines/swapUIMachine"
import type { TokenInfo } from "@src/components/DefuseSDK/types/base"
import {
  type ActorRefFrom,
  assign,
  emit,
  enqueueActions,
  setup,
  stopChild,
} from "xstate"

type IntentSettledEvent = {
  type: "INTENT_SETTLED"
  data: {
    intentHash: string
    txHash: string
    tokenIn: TokenInfo
    tokenOut: TokenInfo
  }
}

type OneClickSettledEvent = {
  type: "ONE_CLICK_SETTLED"
  data: {
    depositAddress: string
    status: string
    tokenIn: TokenInfo
    tokenOut: TokenInfo
  }
}

type PassthroughEvent = IntentSettledEvent | OneClickSettledEvent

export type TrackedSwapRef = {
  id: string
  intentHash: string
  depositAddress?: string
  tokenIn: TokenInfo
  tokenOut: TokenInfo
  totalAmountIn: { amount: bigint; decimals: number }
  totalAmountOut: { amount: bigint; decimals: number }
  createdAt: Date
  completedAt: Date | null
  lastTerminalStatus: string | null
  is1cs: boolean
  isConfidential: boolean
  // Chain-native account that initiated the swap; null when not provided.
  accountId: string | null
  seenOnSwapPage: boolean
  actorRef:
    | ActorRefFrom<typeof intentStatusMachine>
    | ActorRefFrom<typeof oneClickStatusMachine>
  swapJourney?: SwapJourneyProvenance
}

export type SwapTrackerContext = {
  swapRefs: TrackedSwapRef[]
}

export type RegisterSwapParams = {
  intentHash: string
  depositAddress?: string
  tokenIn: TokenInfo
  tokenOut: TokenInfo
  intentDescription: IntentDescription
  is1cs: boolean
  isConfidential?: boolean
  accountId?: string
  swapJourney?: SwapJourneyProvenance | null
}

export const swapTrackerMachine = setup({
  types: {
    context: {} as SwapTrackerContext,
    events: {} as
      | { type: "REGISTER_SWAP"; params: RegisterSwapParams }
      | { type: "DISMISS_SWAP"; id: string }
      | { type: "MARK_SWAP_SEEN"; id: string }
      // Confidential deposits can expire; trackers never set a deadline, so this
      // never fires here — declared only so the actor's parent type is satisfied.
      | OneClickExpiredEvent
      | PassthroughEvent,
    emitted: {} as PassthroughEvent,
  },
  actors: {
    intentStatusActor: intentStatusMachine,
    oneClickStatusActor: oneClickStatusMachine,
  },
  actions: {
    spawnSwapActor: assign({
      swapRefs: ({ context, event, spawn, self }) => {
        const { params } = event as {
          type: "REGISTER_SWAP"
          params: RegisterSwapParams
        }
        if (params.intentDescription.type !== "swap") return context.swapRefs

        const id = params.depositAddress ?? params.intentHash
        if (context.swapRefs.some((s) => s.id === id)) return context.swapRefs

        const swapDescription = params.intentDescription as Extract<
          IntentDescription,
          { type: "swap" }
        >

        const actorRef =
          params.is1cs && params.depositAddress
            ? spawn("oneClickStatusActor", {
                id: `1cs-${id}`,
                input: {
                  parentRef: self,
                  intentHash: params.intentHash,
                  depositAddress: params.depositAddress,
                  tokenIn: params.tokenIn,
                  tokenOut: params.tokenOut,
                  totalAmountIn: swapDescription.totalAmountIn,
                  totalAmountOut: swapDescription.totalAmountOut,
                  isConfidential: params.isConfidential,
                },
              })
            : spawn("intentStatusActor", {
                id: `intent-${id}`,
                input: {
                  parentRef: self,
                  intentHash: params.intentHash,
                  tokenIn: params.tokenIn,
                  tokenOut: params.tokenOut,
                  intentDescription: params.intentDescription,
                  isConfidential: params.isConfidential,
                },
              })

        return [
          {
            id,
            intentHash: params.intentHash,
            depositAddress: params.depositAddress,
            tokenIn: params.tokenIn,
            tokenOut: params.tokenOut,
            totalAmountIn: swapDescription.totalAmountIn,
            totalAmountOut: swapDescription.totalAmountOut,
            createdAt: new Date(),
            completedAt: null,
            lastTerminalStatus: null,
            is1cs: params.is1cs,
            isConfidential: params.isConfidential === true,
            accountId: params.accountId ?? null,
            seenOnSwapPage: false,
            actorRef: actorRef as TrackedSwapRef["actorRef"],
            ...(params.swapJourney != null
              ? { swapJourney: params.swapJourney }
              : {}),
          },
          ...context.swapRefs,
        ]
      },
    }),
    dismissSwapActor: enqueueActions(({ context, enqueue, event }) => {
      const { id } = event as { type: "DISMISS_SWAP"; id: string }
      const swapRef = context.swapRefs.find((s) => s.id === id)
      if (swapRef == null) return

      enqueue(stopChild(swapRef.actorRef))
      enqueue(
        assign({
          swapRefs: ({ context }) =>
            context.swapRefs.filter((s) => s.id !== id),
        })
      )
    }),
    markSwapSeen: assign({
      swapRefs: ({ context, event }) => {
        const { id } = event as { type: "MARK_SWAP_SEEN"; id: string }
        return context.swapRefs.map((s) =>
          s.id === id ? { ...s, seenOnSwapPage: true } : s
        )
      },
    }),
    setCompletedAt: assign({
      swapRefs: ({ context, event }) => {
        const now = new Date()
        if (event.type === "INTENT_SETTLED") {
          return context.swapRefs.map((s) =>
            s.intentHash === event.data.intentHash && s.completedAt == null
              ? { ...s, completedAt: now }
              : s
          )
        }
        if (event.type === "ONE_CLICK_SETTLED") {
          return context.swapRefs.map((s) =>
            s.depositAddress === event.data.depositAddress
              ? {
                  ...s,
                  completedAt: s.completedAt ?? now,
                  lastTerminalStatus: event.data.status,
                }
              : s
          )
        }
        return context.swapRefs
      },
    }),
    passthroughEvent: emit((_, event: PassthroughEvent) => event),
  },
  guards: {
    isPendingTrackedEvent: ({ context, event }) => {
      if (event.type === "INTENT_SETTLED") {
        return context.swapRefs.some(
          (swapRef) =>
            swapRef.intentHash === event.data.intentHash &&
            swapRef.completedAt == null
        )
      }
      if (event.type === "ONE_CLICK_SETTLED") {
        return context.swapRefs.some(
          (swapRef) =>
            swapRef.depositAddress === event.data.depositAddress &&
            swapRef.lastTerminalStatus !== event.data.status
        )
      }
      return false
    },
  },
}).createMachine({
  id: "swapTracker",
  context: { swapRefs: [] },
  on: {
    REGISTER_SWAP: { actions: "spawnSwapActor" },
    DISMISS_SWAP: { actions: "dismissSwapActor" },
    MARK_SWAP_SEEN: { actions: "markSwapSeen" },
    INTENT_SETTLED: {
      guard: "isPendingTrackedEvent",
      actions: [
        "setCompletedAt",
        { type: "passthroughEvent", params: ({ event }) => event },
      ],
    },
    ONE_CLICK_SETTLED: {
      guard: "isPendingTrackedEvent",
      actions: [
        "setCompletedAt",
        { type: "passthroughEvent", params: ({ event }) => event },
      ],
    },
  },
})
